import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Button } from "@mui/material";

import axiosApi from "../../../../core/request";
import { useAppSelector, useLocalStorage } from "../../../../hooks";
import Loader from "../../../../ui/Loader";
import { Text } from "../../../../ui/Text/Text";
import { sendNotification } from "../../../../ui/Toast";

import { WeekendCard } from "./components/WeekendCard";

import styles from "./styles.module.scss";

interface CalendarData {
  allDay: boolean | null;
  description: string | null;
  endDate: string;
  id: number | null;
  startDate: string | null;
  title: string | null;
  type: number | null;
}

export const Holidays = () => {
  const { t } = useTranslation();
  const currentDate = new Date();

  const employeeId = useAppSelector((state) => state.auth.userId);
  const [calendarData, setCalendarData] = useState<CalendarData[]>([]);
  const [metaInfo, setMetaInfo] = useState({
    maxHolidaysPerYear: 0,
    isHolidaysModifiable: false,
    isVacationModifiable: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [addedHolidays, setAddedHolidays] = useState<
    {
      newId: string | null;
      date: Dayjs | null;
      note: string;
    }[]
  >([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const [year, setYear] = useLocalStorage<number>(
    "vacationsSickDaysFilterYear",
    currentDate.getFullYear(),
  );
  const [holidaysToDelete, setHolidaysToDelete] = useState<number[]>([]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const daysOffDto = addedHolidays.map((holiday) => {
        const localStartDate = dayjs(holiday.date).startOf("day");
        const offset = localStartDate.utcOffset();
        const offsetInHours = dayjs().utcOffset() / 60;

        const adjustedDate = localStartDate
          .utc()
          .add(offset + offsetInHours, "minute")
          .format();

        return {
          startDate: adjustedDate,
          finishDate: adjustedDate,
          sign: 3,
          remark: holiday.note || null,
        };
      });

      if (daysOffDto.length > 0) {
        const payload = {
          employeeId,
          daysOffDto,
        };

        await axiosApi.post("/personal-cabinet/calendar/daysoff", payload);
      }

      if (holidaysToDelete.length > 0) {
        await axiosApi.delete("/personal-cabinet/calendar/daysoff", {
          data: { ids: holidaysToDelete },
        });
      }

      sendNotification(t("notifications.successfullySaved"), "success");

      setAddedHolidays([]);
      setHolidaysToDelete([]);
      setIsEdit(false);
      fetchData();
    } catch (error) {
      sendNotification(t("statuses.Error"), "error");
      console.error("Error saving holidays:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNewHoliday = (holiday: {
    date: Dayjs | null;
    note: string;
    newId: string | null;
  }) => {
    setAddedHolidays((prev) => [...prev, holiday]);
  };

  const handleMarkForDelete = (id: number) => {
    setHolidaysToDelete((prev) => [...prev, id]);
    setCalendarData((prev) => prev.filter((item) => item.id !== id));
  };

  const handleRemoveAddedHoliday = (newId: string | null) => {
    setAddedHolidays((prev) =>
      prev.filter((holiday) => holiday.newId !== newId),
    );
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const yearToFetch = `${year}.01.01`;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [calendarRes, metaRes] = await Promise.all([
        axiosApi.get("/personal-cabinet/calendar", {
          params: { employeeId, year, type: 3 },
        }),
        axiosApi.get(`/personal-cabinet/calendar/meta`, {
          params: { employeeId, date: yearToFetch },
        }),
      ]);

      setCalendarData(calendarRes.data);
      setMetaInfo(metaRes.data);
    } catch (error) {
      console.error("Error fetching holidays data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeYear = (year: number | undefined): void => {
    if (year === undefined) {
      return;
    }
    setYear(year);
  };

  useEffect(() => {
    fetchData();
  }, [employeeId, year]);

  return (
    <div>
      {!isLoading && (
        <div className={styles.meta_info}>
          <div>
            <div className={styles.box_year}>
              <ArrowLeftIcon
                htmlColor="gray"
                sx={{ color: "green", fontSize: "40px" }}
                onClick={() => handleChangeYear(year - 1)}
              />
              <p>{year}</p>
              <ArrowRightIcon
                htmlColor="gray"
                sx={{
                  color: "green",
                  fontSize: "40px",
                }}
                onClick={() => handleChangeYear(year + 1)}
              />
            </div>
          </div>
          <div style={{ marginLeft: "50px" }} className={styles.meta_info}>
            {metaInfo.isHolidaysModifiable && (
              <Button
                disabled={isLoading}
                variant="contained"
                onClick={isEdit ? handleSave : () => setIsEdit(!isEdit)}
              >
                {isEdit ? t("buttons.Save") : t("buttons.Edit")}
              </Button>
            )}
            {isEdit && (
              <Button
                disabled={isLoading}
                variant="contained"
                onClick={() => {
                  setIsEdit(!isEdit);
                  setAddedHolidays([]);
                  setHolidaysToDelete([]);
                  fetchData();
                }}
              >
                {t("buttons.Close")}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className={styles.cards_container}>
        {isLoading ? (
          <div
            style={{
              width: "100%",
            }}
          >
            <Loader />
          </div>
        ) : isEdit ? (
          <>
            {metaInfo.isHolidaysModifiable &&
              [...calendarData, ...addedHolidays].length <
              metaInfo.maxHolidaysPerYear && (
                <WeekendCard
                  selectedDates={[
                    ...calendarData.map(
                      (item) =>
                        ({
                          date: item.startDate,
                        }).date,
                    ),
                    ...addedHolidays.map((item) => item.date),
                  ]}
                  isNew
                  onAdd={handleAddNewHoliday}
                />
              )}

            {[...calendarData, ...addedHolidays].map((item: any) => (
              <WeekendCard
                key={item.id || item.newId}
                id={item.id || null}
                date={item.id ? item.startDate : item.date}
                note={item.id ? item.description : item.note}
                onDelete={
                  item.id
                    ? () => handleMarkForDelete(item.id!)
                    : () => handleRemoveAddedHoliday(item.newId)
                }
              />
            ))}
          </>
        ) : (
          <div>
            <div className={styles.summary_table}>
              <table className={styles.summary_table_content}>
                <tbody>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("account.filled-in")}
                    </th>
                    <td>
                      <span className={styles.value_color}>
                        {calendarData.length} {t("account.of")}{" "}
                        {metaInfo.maxHolidaysPerYear}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.sick_leave_table}>
              <table className={styles.sick_leave_table_content}>
                <thead className={styles.table_header}>
                  <tr>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.dates")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.daysCount")}
                    </th>
                    <th style={{ backgroundColor: "white" }}>
                      {t("vacationsSickDays.note")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calendarData.map((item: CalendarData, idx) => {
                    const startDate = moment(item.startDate);
                    const endDate = moment(item.endDate);
                    const daysDifference = endDate.diff(startDate, "days") + 1;
                    const isExpanded = expandedRows.has(item.id!);
                    const description =
                      item.description ||
                      String(t("vacationsSickDays.noReason"));
                    const isLongText = description.length > 50;

                    return (
                      <tr key={item.id}>
                        <td>
                          {startDate.format("DD.MM.YYYY")} -{" "}
                          {endDate.format("DD.MM.YYYY")}
                        </td>
                        <td>{daysDifference}</td>
                        <td className={isExpanded ? styles.expanded_cell : ""}>
                          {isExpanded ? (
                            <div>
                              <div className={styles.full_text}>
                                {description}
                              </div>
                              <button
                                className={styles.collapse_button}
                                onClick={() => toggleRowExpansion(item.id!)}
                              >
                                Свернуть
                              </button>
                            </div>
                          ) : (
                            <div>
                              <div className={styles.truncated_text}>
                                {description.length > 50
                                  ? `${description.substring(0, 50)}...`
                                  : description}
                              </div>
                              {isLongText && (
                                <button
                                  className={styles.expand_button}
                                  onClick={() => toggleRowExpansion(item.id!)}
                                >
                                  Смотреть полностью
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {calendarData.length === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.no_data}>
                        {t("vacationsSickDays.noHolidayData")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
